import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The scene's play, pause and scrub state, shared by the AnimationPlayer
 * component and the Animation tab. It starts stopped, so a loaded scene shows
 * its authored pose (ADR-0011). It tracks one registered player.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
/** 10 Hz reads as smooth for a scrubber without a re-render of every consumer each frame. */
const REPORT_THROTTLE_MS = 100;
const RESET_CLIP = 'RESET';
/** The default clip: autoplay, else the first non-RESET clip, else the first clip, else none. */
function defaultClip(reg) {
    if (reg.autoplay && reg.clips.includes(reg.autoplay))
        return reg.autoplay;
    return reg.clips.find((c) => c !== RESET_CLIP) ?? reg.clips[0] ?? null;
}
const NO_OP = {
    clips: [],
    selectedClip: null,
    autoplayClip: null,
    hasPlayer: false,
    playState: 'stopped',
    time: 0,
    duration: 0,
    playbackSpeed: 1,
    setPlaybackSpeed: () => { },
    loopOverride: 'auto',
    setLoopOverride: () => { },
    registerPlayer: () => () => { },
    play: () => { },
    pause: () => { },
    stop: () => { },
    seek: () => { },
    selectClip: () => { },
    reportTime: () => { },
};
const AnimationTransportContext = createContext(NO_OP);
AnimationTransportContext.displayName = 'AnimationTransportContext';
export function useAnimationTransport() {
    return useContext(AnimationTransportContext);
}
export function AnimationTransportProvider({ children }) {
    const [registration, setRegistration] = useState(null);
    const [selectedClip, setSelectedClip] = useState(null);
    const [playState, setPlayState] = useState('stopped');
    const [time, setTime] = useState(0);
    const [playbackSpeed, setPlaybackSpeedState] = useState(1);
    const [loopOverride, setLoopOverride] = useState('auto');
    const clips = useMemo(() => registration?.clips ?? [], [registration]);
    const hasPlayer = registration !== null;
    const autoplayClip = registration?.autoplay && clips.includes(registration.autoplay) ? registration.autoplay : null;
    const duration = (selectedClip ? registration?.durations[selectedClip] : undefined) ?? 0;
    const registerPlayer = useCallback((reg) => {
        // A new player starts neutral, so no override from the last player applies.
        // Registration and unregistration share one reset, so no edge leaks.
        const resetPlayback = () => {
            setPlayState('stopped');
            setTime(0);
            setPlaybackSpeedState(1);
            setLoopOverride('auto');
        };
        setRegistration(reg);
        setSelectedClip(defaultClip(reg));
        resetPlayback();
        return () => {
            setRegistration(null);
            setSelectedClip(null);
            resetPlayback();
        };
    }, []);
    // A zero, negative or non-finite speed would freeze or reverse playback silently.
    const setPlaybackSpeed = useCallback((speed) => {
        setPlaybackSpeedState(Number.isFinite(speed) && speed > 0 ? speed : 1);
    }, []);
    const play = useCallback(() => {
        if (selectedClip)
            setPlayState('playing');
    }, [selectedClip]);
    const pause = useCallback(() => setPlayState('paused'), []);
    const stop = useCallback(() => {
        setPlayState('stopped');
        setTime(0);
    }, []);
    const clampTime = useCallback((t) => {
        const max = (selectedClip ? registration?.durations[selectedClip] : undefined) ?? 0;
        return Math.max(0, Math.min(t, max));
    }, [registration, selectedClip]);
    const seek = useCallback((t) => setTime(clampTime(t)), [clampTime]);
    const selectClip = useCallback((name) => {
        setSelectedClip(name);
        setTime(0);
    }, []);
    // Called every frame while playing. The throttle keeps the other consumers,
    // the mixer-owning component above all, from re-rendering 60 times a second.
    const lastCommitRef = useRef(0);
    const reportTime = useCallback((t, options) => {
        const now = Date.now();
        if (!options?.immediate && now - lastCommitRef.current < REPORT_THROTTLE_MS)
            return;
        lastCommitRef.current = now;
        setTime(clampTime(t));
    }, [clampTime]);
    const value = useMemo(() => ({
        clips,
        selectedClip,
        autoplayClip,
        hasPlayer,
        playState,
        time,
        duration,
        playbackSpeed,
        setPlaybackSpeed,
        loopOverride,
        setLoopOverride,
        registerPlayer,
        play,
        pause,
        stop,
        seek,
        selectClip,
        reportTime,
    }), [
        clips,
        selectedClip,
        autoplayClip,
        hasPlayer,
        playState,
        time,
        duration,
        playbackSpeed,
        setPlaybackSpeed,
        loopOverride,
        setLoopOverride,
        registerPlayer,
        play,
        pause,
        stop,
        seek,
        selectClip,
        reportTime,
    ]);
    return _jsx(AnimationTransportContext.Provider, { value: value, children: children });
}
//# sourceMappingURL=AnimationTransportContext.js.map